import type { AluminiumAISampleComposition, PETComposition, SpecificationListItem } from 'api';
import { useTranslation } from 'i18n';
import { addError, logger } from 'monitor';
import { NoData } from 'ui';

import { AiLooseModels, isOtherAiModel } from '../../../evidence/types';
import { AiAluSampleBreakdown } from '../AiAluSampleBreakdown/AiAluSampleBreakdown';
import { AiPetSampleBreakdown } from '../AiPetSampleBreakdown/AiPetSampleBreakdown';
import { NoAnalysisAlert } from '../NoAnalysisAlert/NoAnalysisAlert';
import { PETCompositionBreakdown } from '../PETCompositionBreakdown/PETCompositionBreakdown';

const isPetComposition = (composition: AluminiumAISampleComposition | PETComposition): composition is PETComposition =>
  'clear' in composition && 'light_blue' in composition;

export const AiSampleBreakdown = ({
  model,
  composition,
  totalWeight,
  instructions,
}: {
  composition?: AluminiumAISampleComposition | PETComposition;
  model?: AiLooseModels;
  totalWeight?: number;
  instructions?: SpecificationListItem[];
}) => {
  const { t } = useTranslation('features');

  if (isOtherAiModel(model)) {
    logger.info('No results shown for "other" AI models');
    return <NoData>{t('breakdowns.aiSampleBreakdown.analysisNotAvailable')}</NoData>;
  }

  if (!composition && model) {
    logger.warn('No composition provided');
    return <NoAnalysisAlert aiModel={model} />;
  }

  if (!model || !composition) {
    addError(Error('No model provided for composition'));
    return <NoData>{t('breakdowns.aiSampleBreakdown.analysisNotAvailable')}</NoData>;
  }

  const petCompositionCheck = isPetComposition(composition);
  if (model === 'pet_loose' && petCompositionCheck && totalWeight !== undefined) {
    return <AiPetSampleBreakdown composition={composition} totalWeight={totalWeight} instructions={instructions} />;
  }

  if (model === 'pet_loose' && petCompositionCheck && totalWeight === undefined) {
    return <PETCompositionBreakdown composition={composition} instructions={instructions} modelName={model} />;
  }

  if (model === 'alu_loose' && !petCompositionCheck) {
    if (totalWeight) {
      return <AiAluSampleBreakdown totalWeight={totalWeight} composition={composition} instructions={instructions} />;
    } else {
      addError(Error('No total weight provided for alu_loose sample'));
      return <NoAnalysisAlert aiModel={model} />;
    }
  }

  addError(Error('Invalid sample data'), {
    composition,
    model,
    totalWeight,
  });
  return null;
};
